import React, { useState, useEffect, useCallback } from "react";
import { locationApi } from "../api/services";
import { useAuth } from "../context/AuthContext";
import toast, { Toaster } from "react-hot-toast";

// ── Icons ─────────────────────────────────────────────────────────────────────
const Icons = {
  ChevronRight: (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  ),
  BuildingOffice2Icon: (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5h.75m-.75 3h.75m-.75 3h.75" />
    </svg>
  ),
  Square2StackIcon: (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0l-5.571 3-5.571-3" />
    </svg>
  ),
  StopIcon: (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z" />
    </svg>
  ),
  MapPinIcon: (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  ),
  PlusIcon: (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  ),
  PencilIcon: (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
    </svg>
  ),
  TrashIcon: (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  ),
};

const LOCATION_TYPES = ["Building", "Floor", "Room", "Zone", "Section", "Facility", "Rack"];

// ── Recursive tree node ───────────────────────────────────────────────────────
const LocationNode = ({ location, level, onAddChild, onEdit, onDelete, canEdit }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = location.children && location.children.length > 0;

  const iconMap = { Floor: "Square2StackIcon", Room: "StopIcon", Zone: "MapPinIcon", Section: "MapPinIcon" };
  const IconComponent = Icons[iconMap[location.location_type] || "BuildingOffice2Icon"];

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div
        className="flex items-center justify-between py-4 pr-4 hover:bg-gray-50 transition-colors"
        style={{ paddingLeft: `${level * 24 + 16}px` }}
      >
        <div className="flex items-center gap-3 flex-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`p-1 rounded-md hover:bg-gray-200 text-gray-500 transition-transform ${hasChildren ? "" : "invisible"} ${isExpanded ? "rotate-90" : ""}`}
          >
            <Icons.ChevronRight className="w-4 h-4" />
          </button>
          <div className="text-gray-500">
            <IconComponent className="w-5 h-5" />
          </div>
          <span className="font-medium text-gray-800">{location.name}</span>
          <span className="ml-4 px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full border border-gray-200">
            {location.location_type}
          </span>
        </div>

        {canEdit && (
          <div className="flex items-center gap-3 text-gray-400">
            <button onClick={() => onAddChild(location.id)} className="hover:text-blue-600 transition-colors" title="Add Child Location">
              <Icons.PlusIcon className="w-5 h-5" />
            </button>
            {/* FIX: onClick is now wired up — was missing in the original */}
            <button onClick={() => onEdit(location)} className="hover:text-blue-600 transition-colors" title="Edit Location">
              <Icons.PencilIcon className="w-5 h-5" />
            </button>
            <button onClick={() => onDelete(location.id)} className="hover:text-red-600 transition-colors" title="Delete Location">
              <Icons.TrashIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {isExpanded && hasChildren && (
        <div>
          {location.children.map((child) => (
            <LocationNode key={child.id} location={child} level={level + 1}
              onAddChild={onAddChild} onEdit={onEdit} onDelete={onDelete} canEdit={canEdit} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const LocationHierarchy = () => {
  const { activeTenant, hasRole, user } = useAuth();
  // FIX: handle activeTenant as object
  const resolvedTenant = (typeof activeTenant === 'object' ? activeTenant?.id : activeTenant) ?? user?.tenant_id;
  const canEdit = hasRole("admin", "tenant_admin", "super_admin");

  const [locations,    setLocations]    = useState([]);
  const [isLoading,    setIsLoading]    = useState(true);
  const [isModalOpen,  setIsModalOpen]  = useState(false);
  const [isEditing,    setIsEditing]    = useState(false);
  const [editTargetId, setEditTargetId] = useState(null);
  const [parentIdToAdd, setParentIdToAdd] = useState(null);
  const [formError,    setFormError]    = useState('');
  const [locationForm, setLocationForm] = useState({ name: "", location_type: "Building" });

  const fetchLocations = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await locationApi.getAll({ tenantId: resolvedTenant });
      setLocations(response.data?.data || response.data || []);
    } catch {
      toast.error("Failed to load locations");
    } finally {
      setIsLoading(false);
    }
  }, [resolvedTenant]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!locationForm.name) return;
    try {
      if (isEditing) {
        // FIX: pass resolvedTenant so super_admin requests include tenant_id query param
        await locationApi.update(editTargetId, { name: locationForm.name, location_type: locationForm.location_type }, resolvedTenant);
        toast.success("Location updated");
      } else {
        // FIX: pass resolvedTenant as second arg so super_admin requests work
        await locationApi.create({
          name: locationForm.name,
          location_type: locationForm.location_type,
          parent_id: parentIdToAdd,
        }, resolvedTenant);
        toast.success("Location added");
      }
      fetchLocations();
      closeModal();
    } catch (err) {
      setFormError(err.response?.data?.message || err.response?.data?.error || "Failed to save location");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this location and all sub-locations?")) return;
    try {
      // FIX: pass resolvedTenant so super_admin requests include tenant_id query param
      await locationApi.delete(id, resolvedTenant);
      toast.success("Location deleted");
      fetchLocations();
    } catch {
      toast.error("Failed to delete location");
    }
  };

  const openAddModal = (parentId = null) => {
    setIsEditing(false);
    setEditTargetId(null);
    setParentIdToAdd(parentId);
    setLocationForm({ name: "", location_type: parentId === null ? "Building" : "Floor" });
    setFormError('');
    setIsModalOpen(true);
  };

  // FIX: edit handler — opens modal pre-filled with existing location data
  const openEditModal = (location) => {
    setIsEditing(true);
    setEditTargetId(location.id);
    setParentIdToAdd(null);
    setLocationForm({ name: location.name, location_type: location.location_type });
    setFormError('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setEditTargetId(null);
    setParentIdToAdd(null);
    setLocationForm({ name: "", location_type: "Building" });
    setFormError('');
  };

  return (
    <div className="bg-gray-50 min-h-screen p-8 font-sans relative">
      <Toaster position="top-right" />

      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Location Hierarchy</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your location structure (Building → Floor → Room → Zone → Section)</p>
        </div>
        {canEdit && (
          <button
            onClick={() => openAddModal(null)}
            className="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white px-6 py-2.5 rounded-lg font-medium shadow-sm transition-colors"
          >
            <Icons.PlusIcon className="w-5 h-5" />
            Add Location
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[300px]">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-800">Locations</h2>
          <p className="text-xs text-gray-500 mt-1">Click nodes to expand/collapse. Use the + icon to add child locations.</p>
        </div>

        <div className="border-t border-gray-100">
          {isLoading ? (
            <div className="flex justify-center py-12 text-gray-500">Loading hierarchy...</div>
          ) : locations.length > 0 ? (
            locations.map((location) => (
              <LocationNode key={location.id} location={location} level={0}
                onAddChild={openAddModal} onEdit={openEditModal} onDelete={handleDelete} canEdit={canEdit} />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Icons.BuildingOffice2Icon className="w-12 h-12 mb-2 opacity-20" />
              <p>No locations found.</p>
              {canEdit && <p className="text-sm">Click "Add Location" to start building your hierarchy.</p>}
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-lg font-bold text-gray-800">
                {isEditing ? "Edit Location" : "Add New Location"}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                <input
                  type="text" required
                  value={locationForm.name}
                  onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                  placeholder="e.g., Floor 3"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Type</label>
                <select
                  value={locationForm.location_type}
                  onChange={(e) => setLocationForm({ ...locationForm, location_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {LOCATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 px-4 py-2 bg-blue-800 text-white font-medium rounded-lg hover:bg-blue-900 shadow-md">
                  {isEditing ? "Save Changes" : "Add Location"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationHierarchy;